/**
 * The Apple "redeem this code" deep link — per code.
 *
 * Opening `https://apps.apple.com/redeem?ctx=apps&code=XXXX` takes the user straight to
 * the App Store with the code already filled in. The manual route is buried behind the
 * App Store avatar menu → "Redeem Gift Card or Code" → "Enter Code Manually", and one
 * mistyped character wastes a single-use code. So every code gets its own link.
 *
 * This MIRRORS `AppleRedeem` on the backend (CCMS.Api.ApplicationCatalogue), which is the
 * source of truth and sends `redeemUrl` on each code. We build it client-side too so the
 * link is present even when the API predates that field — it is pure string construction
 * from a code we already hold, so there is nothing to wait for. Keep the two in step.
 */

const REDEEM_BASE = 'https://apps.apple.com/redeem?ctx=apps&code=';
const CODE_PLACEHOLDER = /\{code\}/gi;

/**
 * True when a URL is Apple's redeem link (or carries a `{code}` placeholder) — i.e. it is
 * a per-code TEMPLATE, not the app's store page.
 *
 * This matters because App Store Connect hands you the redeem link with an EMPTY `code=`,
 * so it is entirely natural for an admin to paste it into the "Install link" field.
 * Opened as-is it lands on a blank redeem screen, so it must never be offered as a plain
 * "view it in the store" link.
 */
export function isRedeemLink(url?: string | null): boolean {
  if (!url) return false;
  const u = url.trim().toLowerCase();
  return u.includes('/redeem') || u.includes('{code}');
}

/**
 * The redeem link for one code. Prefers the admin's own template when they configured one
 * (so their exact URL shape survives), otherwise Apple's canonical form.
 *
 * @param code       the redemption code
 * @param installUrl the distribution's configured install link, if any
 */
export function buildRedeemLink(code: string, installUrl?: string | null): string | null {
  const trimmed = code?.trim();
  if (!trimmed) return null;

  if (isRedeemLink(installUrl)) {
    return withCode(installUrl!.trim(), trimmed);
  }

  return REDEEM_BASE + encodeURIComponent(trimmed);
}

/**
 * Sets the `code` query parameter on a URL — replacing an existing (usually empty) one, or
 * appending it if absent. Everything else the admin wrote is left alone.
 */
function withCode(url: string, code: string): string {
  const escaped = encodeURIComponent(code);

  if (CODE_PLACEHOLDER.test(url)) {
    CODE_PLACEHOLDER.lastIndex = 0; // the regex is global — reset before reuse
    return url.replace(CODE_PLACEHOLDER, escaped);
  }

  const hashIndex = url.indexOf('#');
  const fragment = hashIndex >= 0 ? url.slice(hashIndex) : '';
  const withoutFragment = hashIndex >= 0 ? url.slice(0, hashIndex) : url;

  const queryIndex = withoutFragment.indexOf('?');
  const path = queryIndex >= 0 ? withoutFragment.slice(0, queryIndex) : withoutFragment;
  const query = queryIndex >= 0 ? withoutFragment.slice(queryIndex + 1) : '';

  const pairs = query ? query.split('&').filter(Boolean) : [];

  let replaced = false;
  for (let i = 0; i < pairs.length; i++) {
    const equalsIndex = pairs[i].indexOf('=');
    const key = equalsIndex >= 0 ? pairs[i].slice(0, equalsIndex) : pairs[i];
    if (key.toLowerCase() === 'code') {
      pairs[i] = `code=${escaped}`;
      replaced = true;
    }
  }

  if (!replaced) pairs.push(`code=${escaped}`);

  return `${path}?${pairs.join('&')}${fragment}`;
}
